<template>
  <Drawer
    v-model:visible="isVisible"
    position="right"
    :style="{ width: 'min(32rem, 100%)' }"
    :show-close-icon="false"
    :pt-options="{ mergeProps: true }"
    :pt="{ content: { class: 'px-6 pt-2' } }"
  >
    <template #header>
      <div class="w-full flex justify-between items-center">
        <h2 class="text-xl font-semibold">Change payment amount or waive</h2>
        <button
          class="p-2 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Close"
          @click="isVisible = false"
        >
          <i class="pi pi-times text-xl" />
        </button>
      </div>
    </template>

    <div class="space-y-8">
      <div class="pb-6 border-b border-gray-200">
        <div class="text-sm font-medium text-gray-500">Current Amount</div>
        <div class="text-lg text-gray-900">
          {{ formatCurrency(currentAmount) }}
        </div>
      </div>

      <div class="flex items-center gap-8 pb-6 border-b border-gray-200">
        <div class="flex items-center gap-2">
          <RadioButton
            v-model="action"
            input-id="action-change"
            :value="ActionEnum.change_amount"
          />
          <label for="action-change" class="cursor-pointer">
            Change Amount
          </label>
        </div>

        <div class="flex items-center gap-2">
          <RadioButton
            v-model="action"
            input-id="action-waive"
            :value="ActionEnum.waive"
          />
          <label for="action-waive" class="cursor-pointer">Waive</label>
        </div>
      </div>

      <div v-if="action === ActionEnum.change_amount">
        <label class="block text-sm font-medium text-gray-700 mb-2">
          New Amount
        </label>
        <InputNumber
          v-model="newAmount"
          mode="currency"
          currency="USD"
          locale="en-US"
          :min="0.01"
          fluid
          show-buttons
          input-class="w-full"
        />
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Reason <span class="text-gray-400">(optional)</span>
        </label>
        <Select
          v-model="tagSlug"
          :options="tagOptions"
          option-label="name"
          option-value="slug"
          placeholder="Why are you changing the amount?"
          show-clear
          class="w-full"
        />
        <p class="text-xs text-gray-500 mt-1">Visible to admins</p>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Note <span class="text-gray-400">(optional)</span>
        </label>
        <Textarea v-model="notes" rows="3" class="w-full" />
        <p class="text-xs text-gray-500 mt-1">Visible to admins</p>
      </div>

      <Button
        label="Save"
        :disabled="!canSubmit"
        :loading="isLoading"
        class="w-full"
        @click="handleSave"
      />
    </div>
  </Drawer>

  <ConfirmChangeAmountDialog
    v-if="showChangeConfirm"
    v-model:visible="showChangeConfirm"
    v-model:apply-to-all="applyToAll"
    :old-amount="currentAmount"
    :new-amount="newAmount ?? 0"
    :is-loading="isLoading"
    @go-back="handleChangeGoBack"
    @confirm="confirmChangeAmount"
  />

  <ConfirmWaivePaymentDialog
    v-if="showWaiveConfirm"
    v-model:visible="showWaiveConfirm"
    v-model:apply-to-all="applyToAll"
    :is-loading="isLoading"
    @go-back="handleWaiveGoBack"
    @confirm="confirmWaive"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import Drawer from "primevue/drawer";
import RadioButton from "primevue/radiobutton";
import InputNumber from "primevue/inputnumber";
import Select from "primevue/select";
import Textarea from "primevue/textarea";
import Button from "primevue/button";
import { useToast } from "primevue/usetoast";
import { ActionEnum } from "@/modules/payments/utils/amendmentAction";
import { useAmendPayment } from "../composables/useAmendPayment";
import { PLATFORM_AMENDMENT_TAGS } from "../constants/amendmentTags";
import { getFormattedCurrency } from "@/shared/utils/helpers";
import ConfirmChangeAmountDialog from "./ConfirmChangeAmountDialog.vue";
import ConfirmWaivePaymentDialog from "./ConfirmWaivePaymentDialog.vue";

interface Props {
  paymentId: string;
  currentAmount: number | string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  success: [];
}>();

const isVisible = defineModel<boolean>("visible", { required: true });

const toast = useToast();
const { isLoading, amendPayment } = useAmendPayment();

const action = ref<ActionEnum>(ActionEnum.change_amount);
const newAmount = ref<number | null>(null);
const tagSlug = ref<string | null>(null);
const notes = ref("");
const applyToAll = ref(false);

const showChangeConfirm = ref(false);
const showWaiveConfirm = ref(false);

const tagOptions = computed(() => [...PLATFORM_AMENDMENT_TAGS]);

const formatCurrency = (value: number | string) => {
  const num = typeof value === "string" ? parseFloat(value) : value;
  return getFormattedCurrency(Number.isFinite(num) ? num : 0);
};

// Reset state whenever the drawer opens.
watch(
  isVisible,
  (visible) => {
    if (visible) {
      action.value = ActionEnum.change_amount;
      newAmount.value = null;
      tagSlug.value = null;
      notes.value = "";
      applyToAll.value = false;
    }
  },
  { immediate: true }
);

const canSubmit = computed(() => {
  if (isLoading.value) return false;
  if (action.value === ActionEnum.change_amount) {
    return newAmount.value !== null && newAmount.value > 0;
  }
  return true; // waive doesn't require new_amount
});

// Reset applyToAll when returning to the drawer so each confirm step starts
// unchecked — the single ref is shared by both confirm dialogs.
const handleChangeGoBack = () => {
  showChangeConfirm.value = false;
  applyToAll.value = false;
};

const handleWaiveGoBack = () => {
  showWaiveConfirm.value = false;
  applyToAll.value = false;
};

const handleSave = () => {
  if (!canSubmit.value) return;
  if (action.value === ActionEnum.waive) {
    showWaiveConfirm.value = true;
  } else {
    showChangeConfirm.value = true;
  }
};

const confirmChangeAmount = async () => {
  if (newAmount.value === null) return;
  const ok = await amendPayment({
    scheduledPaymentId: props.paymentId,
    action: ActionEnum.change_amount,
    newAmount: newAmount.value,
    tagSlug: tagSlug.value,
    notes: notes.value,
    applyToAll: applyToAll.value,
  });
  finishAmend(ok, "Payment amount updated");
};

const confirmWaive = async () => {
  const ok = await amendPayment({
    scheduledPaymentId: props.paymentId,
    action: ActionEnum.waive,
    tagSlug: tagSlug.value,
    notes: notes.value,
    applyToAll: applyToAll.value,
  });
  finishAmend(ok, "Payment waived");
};

const finishAmend = (ok: boolean, successDetail: string) => {
  showChangeConfirm.value = false;
  showWaiveConfirm.value = false;
  if (ok) {
    toast.add({
      severity: "success",
      summary: "Saved",
      detail: successDetail,
      life: 3000,
    });
    isVisible.value = false;
    emit("success");
  } else {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to amend payment",
      life: 5000,
    });
  }
};
</script>
